import type { MailboxInfo } from 'owa-client-types';
import { isGlobalFeatureEnabled, type GlobalFeatureName } from 'owa-feature-flags';
import { copilotSettingsStore } from 'owa-copilot-settings-store';
import { isCopilotLicensedAndEnabledUser } from './isCopilotLicensedAndEnabledUser';

export interface FrontierFlightConfig {
    publicFlight: GlobalFeatureName;
    privateTapFlight: GlobalFeatureName;
    privateStagingFlight: GlobalFeatureName;
    privateInfluencerFlight: GlobalFeatureName;
}

function isFrontierPublicEnabled(mailboxInfo: MailboxInfo, flight: GlobalFeatureName): boolean {
    const store = copilotSettingsStore(mailboxInfo);
    return store.isFrontierPublicUser && isGlobalFeatureEnabled(flight);
}

function isFrontierPrivateTAPEnabled(mailboxInfo: MailboxInfo, flight: GlobalFeatureName): boolean {
    const store = copilotSettingsStore(mailboxInfo);
    return store.isFrontierPrivateTAPUser && isGlobalFeatureEnabled(flight);
}

function isFrontierStagingEnabled(mailboxInfo: MailboxInfo, flight: GlobalFeatureName): boolean {
    const store = copilotSettingsStore(mailboxInfo);
    return store.isFrontierStagingUser && isGlobalFeatureEnabled(flight);
}

function isFrontierInfluencerEnabled(mailboxInfo: MailboxInfo, flight: GlobalFeatureName): boolean {
    const store = copilotSettingsStore(mailboxInfo);
    return store.isFrontierInfluencerUser && isGlobalFeatureEnabled(flight);
}

/**
 * Generic utility for checking if a copilot feature is enabled for any frontier audience.
 * Requires a copilot license, then checks frontier store properties with per-type feature flights.
 * Each frontier type has its own flight (OFF by default, turn ON to enable).
 *
 * Usage: call this with your feature's frontier flight names.
 * Example:
 * ```
 * isEnabledForFrontierCopilotAudience(mailboxInfo, {
 *     publicFlight: 'my-feature-frontier-public',
 *     privateTapFlight: 'my-feature-frontier-private-tap',
 *     privateStagingFlight: 'my-feature-frontier-private-staging',
 *     privateInfluencerFlight: 'my-feature-frontier-private-influencer',
 * });
 * ```
 */
export function isEnabledForFrontierCopilotAudience(
    mailboxInfo: MailboxInfo,
    flights: FrontierFlightConfig
): boolean {
    if (!isCopilotLicensedAndEnabledUser(mailboxInfo)) {
        return false;
    }
    return (
        isFrontierPublicEnabled(mailboxInfo, flights.publicFlight) ||
        isFrontierPrivateTAPEnabled(mailboxInfo, flights.privateTapFlight) ||
        isFrontierStagingEnabled(mailboxInfo, flights.privateStagingFlight) ||
        isFrontierInfluencerEnabled(mailboxInfo, flights.privateInfluencerFlight)
    );
}
